/**
 * Edit-mode seeder — backend time slots → internal `DayStateMap`.
 *
 * Accepts a flat list of `{start, end}` ISO pairs (what the payload
 * hydrator produces from `/ats_schedules/:id`'s `time_slots`). Groups
 * by date in the calendar's timezone and seeds each day with the
 * ranges as `'custom'` picks only.
 *
 * `autoFillRequested` is intentionally `false`: backend `time_slots`
 * are the user's final committed picks, not a "fill this day" intent.
 * Setting it `true` would cause the merger to layer pipeline-generated
 * slots on top of the 3 saved picks — rendering 13-21 slots instead
 * of the 3 the user actually chose.
 */
import moment from 'moment-timezone';
import type { DayStateMap } from './types';
import type { SelfSlot, SlotOverrideType } from '../types';

export interface InitialSlotsPayload {
  /** ISO timestamp — the source of truth. Timezone offset preserved
   *  so the slot lands on the correct grid cell regardless of the
   *  user's browser tz. */
  start: string;
  end: string;
  /** "Send additional slots" flow only — flips to `true` on the slot
   *  the candidate already booked so the calendar can highlight it. */
  isBooked?: boolean;
  /** Backend override flags from `/ats_schedules/:id`. Forwarded to
   *  the seeded `SelfSlot` so payload build can preserve them
   *  unchanged for unmoved slots (`_fromServer` gate). */
  is_custom?: boolean;
  override_type?: SlotOverrideType;
}

export function seedFromInitial(
  initial?: InitialSlotsPayload[],
  timezone?: string
): DayStateMap {
  const out: DayStateMap = {};
  if (!initial || initial.length === 0) return out;
  const tz = timezone ?? moment.tz.guess();

  for (const slot of initial) {
    if (!slot.start || !slot.end) continue;
    const startM = moment.tz(slot.start, tz);
    const endM = moment.tz(slot.end, tz);
    if (!startM.isValid() || !endM.isValid()) continue;
    const dateKey = startM.format('YYYY-MM-DD');
    const pick: SelfSlot = {
      id: `seed-${slot.start}-${slot.end}`,
      start: slot.start,
      end: slot.end,
      dateKey,
      origin: 'custom',
      // Mark as server-sourced so the payload mapper preserves the
      // hydrated override flags verbatim. `moveSlot` clears this on
      // any user mutation, after which the mapper recomputes.
      _fromServer: true,
      ...(slot.isBooked ? { isBooked: true } : {}),
      ...(slot.is_custom !== undefined ? { is_custom: slot.is_custom } : {}),
      ...(slot.override_type ? { override_type: slot.override_type } : {}),
    };
    const existing = out[dateKey];
    if (existing) {
      existing.customPicks.push(pick);
    } else {
      out[dateKey] = {
        customPicks: [pick],
        droppedIds: new Set(),
        autoFillRequested: false,
      };
    }
  }

  return out;
}
